import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { toast } from "react-hot-toast";
import {
  InformationCircleIcon,
  SparklesIcon,
  PencilSquareIcon,
} from "@heroicons/react/24/outline";
import { chemicalInventoryApi } from "../../../api/chemical-inventory";
import { ResourceLoading } from "../../utils/ResourceLoading";
import { PMField } from "../../utils/PMField";
import { PMDialog } from "../../utils/PMDialog";
import { PMTabs } from "../../utils/PMTabs";
import { QuillEditor } from "../../utils/quill-editor";
import { NotesDeluxeSection } from "../notes/notesDeluxeSection";
import { DocumentManagerV6 } from "../Media/DocumentManagerV6";
import PMCombobox from "../../utils/PMCombobox";
import { ProductSelector } from "../../utils/Selector/productSelector";
import { HazardSymbolsSelector } from "./hazard-symbols-selector";
import { ProtectiveEquipmentSelector } from "./protective-equipment-selector";
import { ChemicalInventoryAiDropzone } from "./chemical-inventory-ai-dropzone";
import {
  analyzeDocumentHasFilledFields,
  analyzeDocumentHasIdentity,
  analyzeDocumentSucceeded,
  analyzeEmptyCopyKey,
  suggestedFromAnalyzeResponse,
} from "./chemicalInventoryAnalyze";

export const ChemicalInventoryForm = ({
  open,
  onClose,
  chemicalInventory = null,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [selectedTab, setSelectedTab] = useState(0);
  const [createStep, setCreateStep] = useState(chemicalInventory ? "form" : "choose");
  const [aiDraft, setAiDraft] = useState(null);
  const [aiDocumentId, setAiDocumentId] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSelectedTab(0);
    setAiDraft(null);
    setAiDocumentId(null);
    setAnalyzing(false);
    setCreateStep(chemicalInventory ? "form" : "choose");
  }, [open, chemicalInventory]);

  const validationSchema = Yup.object({
    product_id: Yup.number().nullable(),
    name: Yup.string().required(t("Navn er påkrevd")),
    cas_number: Yup.string().required(t("CAS-nummer er påkrevd")),
    chemical_formula: Yup.string(),
    molecular_weight: Yup.string(),
    purity: Yup.string(),
    physical_state: Yup.string(),
    color: Yup.string(),
    odor: Yup.string(),
    hms_level: Yup.number().min(1).max(4),
    status: Yup.string().required(),
    storage_location: Yup.string(),
    notes: Yup.string(),
  });

  const initialValues = {
    product_id: chemicalInventory?.product_id || "",
    name: aiDraft?.name || chemicalInventory?.name || "",
    cas_number: aiDraft?.cas_number || chemicalInventory?.cas_number || "",
    chemical_formula: aiDraft?.chemical_formula || chemicalInventory?.chemical_formula || "",
    molecular_weight: aiDraft?.molecular_weight || chemicalInventory?.molecular_weight || "",
    purity: aiDraft?.purity || chemicalInventory?.purity || "",
    physical_state: aiDraft?.physical_state || chemicalInventory?.physical_state || "",
    color: aiDraft?.color || chemicalInventory?.color || "",
    odor: aiDraft?.odor || chemicalInventory?.odor || "",
    hms_level: parseInt(aiDraft?.hms_level || chemicalInventory?.hms_level) || 1,
    status: chemicalInventory?.status || "active",
    version: chemicalInventory?.version || 1,
    hazard_symbols: chemicalInventory?.hazard_symbols || [],
    protective_equipment: chemicalInventory?.protective_equipment || [],
    hazard_symbols_data: aiDraft?.hazard_symbols_data || chemicalInventory?.hazard_symbols_data || "",
    hazard_statements: aiDraft?.hazard_statements || chemicalInventory?.hazard_statements || "",
    precautionary_statements:
      aiDraft?.precautionary_statements || chemicalInventory?.precautionary_statements || "",
    signal_word: aiDraft?.signal_word || chemicalInventory?.signal_word || "",
    hazard_classification: aiDraft?.hazard_classification || chemicalInventory?.hazard_classification || "",
    general_use: aiDraft?.general_use || chemicalInventory?.general_use || "",
    fire_measures: aiDraft?.fire_measures || chemicalInventory?.fire_measures || "",
    spill_measures: aiDraft?.spill_measures || chemicalInventory?.spill_measures || "",
    first_aid: aiDraft?.first_aid || chemicalInventory?.first_aid || "",
    storage_conditions: aiDraft?.storage_conditions || chemicalInventory?.storage_conditions || "",
    personal_protection: aiDraft?.personal_protection || chemicalInventory?.personal_protection || "",
    exposure_limits: aiDraft?.exposure_limits || chemicalInventory?.exposure_limits || "",
    stability_reactivity: aiDraft?.stability_reactivity || chemicalInventory?.stability_reactivity || "",
    toxicological_info: aiDraft?.toxicological_info || chemicalInventory?.toxicological_info || "",
    ecological_info: aiDraft?.ecological_info || chemicalInventory?.ecological_info || "",
    disposal_considerations:
      aiDraft?.disposal_considerations || chemicalInventory?.disposal_considerations || "",
    transport_info: aiDraft?.transport_info || chemicalInventory?.transport_info || "",
    general_use_risk: chemicalInventory?.general_use_risk || "",
    per_operation_risk: chemicalInventory?.per_operation_risk || "",
    risk_assessment_documents:
      chemicalInventory?.risk_assessment_documents || "",
    location_specific_risk: chemicalInventory?.location_specific_risk || "",
    storage_location: aiDraft?.storage_location || chemicalInventory?.storage_location || "",
    last_review_date: chemicalInventory?.last_review_date || "",
    next_review_date: chemicalInventory?.next_review_date || "",
    notes: aiDraft?.notes || chemicalInventory?.notes || "",
    usage_instructions: aiDraft?.usage_instructions || chemicalInventory?.usage_instructions || "",
  };

  const handleSubmit = async (values) => {
    try {
      setLoading(true);

      if (chemicalInventory) {
        await chemicalInventoryApi.updateChemicalInventory(
          chemicalInventory.id,
          values
        );
        toast.success(t("chemical.inventory.update"));
      } else {
        const created = await chemicalInventoryApi.createChemicalInventory(values);
        const createdId = created?.data?.id ?? created?.id;
        if (!createdId) {
          throw new Error("missing_chemical_id");
        }
        if (aiDocumentId) {
          try {
            await chemicalInventoryApi.addDocument(createdId, {
              document_id: aiDocumentId,
              document_type: "safety_data_sheet",
              title: t("chemical.inventory.document_type.safety_data_sheet"),
            });
          } catch (attachErr) {
            console.error("Error attaching SDS:", attachErr);
            toast.error(t("chemical.inventory.sds_attach_error"));
            onSuccess?.();
            onClose();
            return;
          }
        }
        toast.success(t("chemical.inventory.create"));
      }
      onSuccess?.();
      onClose();
    } catch (err) {
      console.error("Error saving chemical inventory:", err);
      toast.error(t("Kunne ikke lagre stoffkartotek"));
    } finally {
      setLoading(false);
    }
  };

  const analyzeUploadedFile = async (file, errorCode) => {
    if (errorCode === "too_large") {
      toast.error(t("chemical.inventory.ai_file_too_large"));
      return;
    }
    if (errorCode === "invalid_type") {
      toast.error(t("chemical.inventory.ai_file_invalid"));
      return;
    }
    if (!file || analyzing) return;

    setAnalyzing(true);
    try {
      const response = await chemicalInventoryApi.analyzeDocument(file);
      if (!analyzeDocumentSucceeded(response)) {
        toast.error(
          response?.errors?.[0] || t("chemical.inventory.ai_failed")
        );
        return;
      }
      const suggested = suggestedFromAnalyzeResponse(response) || {};
      const documentId = response?.data?.document_id;
      if (documentId) setAiDocumentId(documentId);
      if (!analyzeDocumentHasFilledFields(suggested)) {
        setAiDraft(null);
        setCreateStep("form");
        toast.error(t(analyzeEmptyCopyKey(response?.meta?.empty_reason)));
        return;
      }
      setAiDraft(suggested);
      setCreateStep("form");
      toast.success(
        analyzeDocumentHasIdentity(suggested)
          ? t("chemical.inventory.ai_ready")
          : t("chemical.inventory.ai_ready_partial")
      );
    } catch (err) {
      const message =
        err?.response?.data?.errors?.[0] ||
        err?.message ||
        t("chemical.inventory.ai_failed");
      toast.error(message);
    } finally {
      setAnalyzing(false);
    }
  };

  const handleProductChange = (product, setFieldValue) => {
    if (product) {
      // Auto-fill basic information from product
      setFieldValue("name", product.title || "");
      setFieldValue("cas_number", product.cas_number || "");
      setFieldValue("chemical_formula", product.chemical_formula || "");
      setFieldValue("molecular_weight", product.molecular_weight || "");
      setFieldValue("purity", product.purity || "");
      setFieldValue("physical_state", product.physical_state || "");
      setFieldValue("color", product.color || "");
      setFieldValue("odor", product.odor || "");
    }
  };

  const renderInfoTooltip = (text) => (
    <div className="group relative inline-block">
      <InformationCircleIcon className="h-4 w-4 text-gray-400 ml-1 cursor-help" />
      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
        {text}
        <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
      </div>
    </div>
  );

  const renderFieldWithInfo = (fieldProps, infoText) => {
    const { label, ...rest } = fieldProps;
    return (
      <div>
        <div className="flex items-center mb-1">
          <label className="block text-sm font-medium text-gray-700">
            {label}
          </label>
          {renderInfoTooltip(infoText)}
        </div>
        <PMField {...rest} />
      </div>
    );
  };

  const renderComboboxWithInfo = (comboboxProps, infoText) => {
    const { label, ...rest } = comboboxProps;
    return (
      <div>
        <div className="flex items-center mb-1">
          <label className="block text-sm font-medium text-gray-700">
            {label}
          </label>
          {renderInfoTooltip(infoText)}
        </div>
        <PMCombobox {...rest} />
      </div>
    );
  };

  const renderTabs = (formikProps) => {
    const tabs = [
      {
        name: t("chemical.inventory.basic_info"),
        component: (
          <div className="space-y-6">
            <div className="grid grid-cols-6 gap-6">
              <div className="col-span-6">
                <ProductSelector
                  name="product_id"
                  label={t("Produkt")}
                  selected={chemicalInventory?.product}
                  setFieldValue={formikProps.setFieldValue}
                  values={formikProps.values}
                  errors={formikProps.errors}
                  touched={formikProps.touched}
                  onChange={(product) =>
                    handleProductChange(product, formikProps.setFieldValue)
                  }
                />
              </div>
              <div className="col-span-6 sm:col-span-3">
                <PMField
                  name="name"
                  type="text"
                  label={t("Navn")}
                  required
                  {...formikProps}
                />
              </div>
              <div className="col-span-6 sm:col-span-3">
                {renderFieldWithInfo(
                  {
                    name: "cas_number",
                    type: "text",
                    label: t("chemical.inventory.cas_number"),
                    required: true,
                    ...formikProps,
                  },
                  "CAS-nummer er et unikt identifikasjonsnummer for kjemiske stoffer, utviklet av Chemical Abstracts Service"
                )}
              </div>
              <div className="col-span-6 sm:col-span-3">
                {renderFieldWithInfo(
                  {
                    name: "chemical_formula",
                    type: "text",
                    label: t("chemical.inventory.chemical_formula"),
                    ...formikProps,
                  },
                  "Den kjemiske formelen som viser hvilke atomer som inngår i stoffet"
                )}
              </div>
              <div className="col-span-6 sm:col-span-3">
                {renderFieldWithInfo(
                  {
                    name: "molecular_weight",
                    type: "text",
                    label: t("chemical.inventory.molecular_weight"),
                    ...formikProps,
                  },
                  "Molekylvekt i gram per mol (g/mol)"
                )}
              </div>
              <div className="col-span-6 sm:col-span-2">
                <PMField
                  name="purity"
                  type="text"
                  label={t("chemical.inventory.purity")}
                  {...formikProps}
                />
              </div>
              <div className="col-span-6 sm:col-span-2">
                <PMField
                  name="physical_state"
                  type="text"
                  label={t("chemical.inventory.physical_state")}
                  {...formikProps}
                />
              </div>
              <div className="col-span-6 sm:col-span-2">
                {renderComboboxWithInfo(
                  {
                    label: t("chemical.inventory.hms_level"),
                    name: "hms_level",
                    options: [
                      {
                        id: 1,
                        title: t("chemical.inventory.hms_level.1"),
                        value: 1,
                      },
                      {
                        id: 2,
                        title: t("chemical.inventory.hms_level.2"),
                        value: 2,
                      },
                      {
                        id: 3,
                        title: t("chemical.inventory.hms_level.3"),
                        value: 3,
                      },
                      {
                        id: 4,
                        title: t("chemical.inventory.hms_level.4"),
                        value: 4,
                      },
                    ],
                    value: parseInt(formikProps.values.hms_level) || 1,
                    onChange: (value) =>
                      formikProps.setFieldValue(
                        "hms_level",
                        parseInt(value) || 1
                      ),
                    setFieldValue: formikProps.setFieldValue,
                    values: formikProps.values,
                    errors: formikProps.errors,
                    touched: formikProps.touched,
                  },
                  "HMS-nivå indikerer farenivået til stoffet: 1=Lav, 2=Medium, 3=Høy, 4=Kritisk"
                )}
              </div>
              <div className="col-span-6 sm:col-span-3">
                <PMField
                  name="color"
                  type="text"
                  label={t("chemical.inventory.color")}
                  {...formikProps}
                />
              </div>
              <div className="col-span-6 sm:col-span-3">
                <PMField
                  name="odor"
                  type="text"
                  label={t("chemical.inventory.odor")}
                  {...formikProps}
                />
              </div>
              <div className="col-span-6 sm:col-span-3">
                <PMCombobox
                  label={t("chemical.inventory.status")}
                  name="status"
                  options={[
                    {
                      id: "active",
                      title: t("chemical.inventory.status.active"),
                      value: "active",
                    },
                    {
                      id: "inactive",
                      title: t("chemical.inventory.status.inactive"),
                      value: "inactive",
                    },
                    {
                      id: "archived",
                      title: t("chemical.inventory.status.archived"),
                      value: "archived",
                    },
                  ]}
                  value={formikProps.values.status}
                  onChange={(value) =>
                    formikProps.setFieldValue("status", value)
                  }
                  setFieldValue={formikProps.setFieldValue}
                  values={formikProps.values}
                  errors={formikProps.errors}
                  touched={formikProps.touched}
                />
              </div>
              <div className="col-span-6 sm:col-span-3">
                <PMField
                  name="storage_location"
                  type="text"
                  label={t("chemical.inventory.storage_location")}
                  {...formikProps}
                />
              </div>
            </div>
          </div>
        ),
      },
      {
        name: t("chemical.inventory.safety_info"),
        component: (
          <div className="space-y-6">
            <div className="grid grid-cols-6 gap-6">
              <div className="col-span-6">
                <HazardSymbolsSelector
                  value={formikProps.values.hazard_symbols}
                  onChange={(symbols) =>
                    formikProps.setFieldValue("hazard_symbols", symbols)
                  }
                />
              </div>
              <div className="col-span-6">
                <QuillEditor
                  name="hazard_statements"
                  label={t("chemical.inventory.hazard_statements")}
                  {...formikProps}
                />
              </div>
              <div className="col-span-6">
                <QuillEditor
                  name="precautionary_statements"
                  label={t("chemical.inventory.precautionary_statements")}
                  {...formikProps}
                />
              </div>
              <div className="col-span-6 sm:col-span-3">
                <PMField
                  name="signal_word"
                  type="text"
                  label={t("chemical.inventory.signal_word")}
                  {...formikProps}
                />
              </div>
              <div className="col-span-6 sm:col-span-3">
                <PMField
                  name="hazard_classification"
                  type="text"
                  label={t("chemical.inventory.hazard_classification")}
                  {...formikProps}
                />
              </div>
              <div className="col-span-6">
                <QuillEditor
                  name="general_use"
                  label={t("chemical.inventory.general_use")}
                  {...formikProps}
                />
              </div>
              <div className="col-span-6">
                <QuillEditor
                  name="fire_measures"
                  label={t("chemical.inventory.fire_measures")}
                  {...formikProps}
                />
              </div>
              <div className="col-span-6">
                <QuillEditor
                  name="spill_measures"
                  label={t("chemical.inventory.spill_measures")}
                  {...formikProps}
                />
              </div>
              <div className="col-span-6">
                <QuillEditor
                  name="first_aid"
                  label={t("chemical.inventory.first_aid")}
                  {...formikProps}
                />
              </div>
              <div className="col-span-6">
                <QuillEditor
                  name="storage_conditions"
                  label={t("chemical.inventory.storage_conditions")}
                  {...formikProps}
                />
              </div>
              <div className="col-span-6">
                <ProtectiveEquipmentSelector
                  value={formikProps.values.protective_equipment}
                  onChange={(equipment) =>
                    formikProps.setFieldValue("protective_equipment", equipment)
                  }
                />
              </div>
              <div className="col-span-6">
                <QuillEditor
                  name="exposure_limits"
                  label={t("chemical.inventory.exposure_limits")}
                  {...formikProps}
                />
              </div>
              <div className="col-span-6">
                <QuillEditor
                  name="stability_reactivity"
                  label={t("chemical.inventory.stability_reactivity")}
                  {...formikProps}
                />
              </div>
              <div className="col-span-6">
                <QuillEditor
                  name="toxicological_info"
                  label={t("chemical.inventory.toxicological_info")}
                  {...formikProps}
                />
              </div>
              <div className="col-span-6">
                <QuillEditor
                  name="ecological_info"
                  label={t("chemical.inventory.ecological_info")}
                  {...formikProps}
                />
              </div>
              <div className="col-span-6">
                <QuillEditor
                  name="disposal_considerations"
                  label={t("chemical.inventory.disposal_considerations")}
                  {...formikProps}
                />
              </div>
              <div className="col-span-6">
                <QuillEditor
                  name="transport_info"
                  label={t("chemical.inventory.transport_info")}
                  {...formikProps}
                />
              </div>
            </div>
          </div>
        ),
      },

      {
        name: t("chemical.inventory.risk_assessment"),
        component: (
          <div className="space-y-6">
            <div className="grid grid-cols-6 gap-6">
              <div className="col-span-6">
                <QuillEditor
                  name="general_use_risk"
                  label={t("chemical.inventory.general_use_risk")}
                  {...formikProps}
                />
              </div>
              <div className="col-span-6">
                <QuillEditor
                  name="per_operation_risk"
                  label={t("chemical.inventory.per_operation_risk")}
                  {...formikProps}
                />
              </div>
              <div className="col-span-6">
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    {t("chemical.inventory.risk_assessment_documents")}
                  </label>
                  {chemicalInventory?.id ? (
                    <DocumentManagerV6
                      module="chemical_inventory"
                      moduleParam={chemicalInventory.id}
                      mode="inline"
                      allowCopy={false}
                      allowMove={false}
                    />
                  ) : (
                    <p className="text-sm text-slate-500">{t("hse.upload.save_first")}</p>
                  )}
                </div>
              </div>
              <div className="col-span-6">
                <QuillEditor
                  name="location_specific_risk"
                  label={t("chemical.inventory.location_specific_risk")}
                  {...formikProps}
                />
              </div>
              <div className="col-span-6">
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    {t("chemical.inventory.usage_instructions")}
                  </label>
                  {chemicalInventory?.id ? (
                    <DocumentManagerV6
                      module="chemical_inventory"
                      moduleParam={chemicalInventory.id}
                      mode="inline"
                      allowCopy={false}
                      allowMove={false}
                    />
                  ) : (
                    <p className="text-sm text-slate-500">{t("hse.upload.save_first")}</p>
                  )}
                </div>
              </div>
              <div className="col-span-6 sm:col-span-3">
                <PMField
                  name="last_review_date"
                  type="date"
                  label={t("chemical.inventory.last_review_date")}
                  {...formikProps}
                />
              </div>
              <div className="col-span-6 sm:col-span-3">
                <PMField
                  name="next_review_date"
                  type="date"
                  label={t("chemical.inventory.next_review_date")}
                  {...formikProps}
                />
              </div>
            </div>
          </div>
        ),
      },
      {
        name: t("chemical.inventory.notes"),
        component: chemicalInventory?.id ? (
          <div className="space-y-6">
            <NotesDeluxeSection
              module="chemical_inventory"
              module_param={chemicalInventory.id}
            />
          </div>
        ) : (
          <div className="space-y-6">
            <div className="text-center py-8 text-gray-500">
              <p>
                {t("Notater kan kun legges til etter at stoffet er opprettet")}
              </p>
            </div>
          </div>
        ),
      },
    ];

    return tabs;
  };

  const dialogTitle =
    chemicalInventory
      ? t("chemical.inventory.edit")
      : createStep === "upload"
      ? t("chemical.inventory.create_with_ai")
      : t("chemical.inventory.new");

  if (createStep === "choose") {
    return (
      <PMDialog open={open} onClose={onClose} title={dialogTitle} size="lg">
        <div className="p-6 space-y-4">
          <p className="text-sm text-slate-600">{t("chemical.inventory.ai_choose_lead")}</p>
          <div className="grid gap-3 sm:grid-cols-2">
            <button
              type="button"
              onClick={() => setCreateStep("upload")}
              className="rounded-2xl border border-[#BFDCF7] bg-[#EAF3FD] p-5 text-left hover:border-[#1463C7] focus:outline-none focus:ring-2 focus:ring-[#1463C7]"
            >
              <SparklesIcon className="mb-3 h-6 w-6 text-[#1463C7]" />
              <div className="text-sm font-semibold text-slate-900">
                {t("chemical.inventory.create_with_ai")}
              </div>
              <p className="mt-1 text-sm text-slate-600">
                {t("chemical.inventory.ai_upload_lead")}
              </p>
            </button>
            <button
              type="button"
              onClick={() => setCreateStep("form")}
              className="rounded-2xl border border-slate-200 bg-white p-5 text-left hover:border-slate-400 focus:outline-none focus:ring-2 focus:ring-[#1463C7]"
            >
              <PencilSquareIcon className="mb-3 h-6 w-6 text-slate-500" />
              <div className="text-sm font-semibold text-slate-900">
                {t("chemical.inventory.create_manual")}
              </div>
              <p className="mt-1 text-sm text-slate-600">
                {t("chemical.inventory.create_manual_lead")}
              </p>
            </button>
          </div>
        </div>
      </PMDialog>
    );
  }

  if (createStep === "upload") {
    return (
      <PMDialog open={open} onClose={onClose} title={dialogTitle} size="lg">
        <div className="p-6 space-y-4">
          <p className="text-sm text-slate-600">{t("chemical.inventory.ai_upload_lead")}</p>
          {analyzing ? (
            <div className="rounded-2xl border border-[#BFDCF7] bg-[#EAF3FD] p-8 text-center">
              <ResourceLoading size="base" />
              <p className="mt-3 text-sm font-medium text-[#0B3A78]">
                {t("chemical.inventory.ai_analyzing")}
              </p>
            </div>
          ) : (
            <ChemicalInventoryAiDropzone onFile={analyzeUploadedFile} />
          )}
          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => setCreateStep("choose")}
              disabled={analyzing}
              className="px-4 py-2 text-sm font-medium text-slate-700 disabled:opacity-50"
            >
              {t("chemical.inventory.ai_back")}
            </button>
          </div>
        </div>
      </PMDialog>
    );
  }

  return (
    <PMDialog
      open={open}
      onClose={onClose}
      title={dialogTitle}
      size="6xl"
    >
      <Formik
        initialValues={initialValues}
        enableReinitialize
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {(formikProps) => (
          <Form className="p-6">
            {analyzeDocumentHasFilledFields(aiDraft) ? (
              <div className="mb-4 rounded-xl border border-[#BFDCF7] bg-[#EAF3FD] px-4 py-3 text-sm text-[#0B3A78]">
                {t("chemical.inventory.ai_review_hint")}
              </div>
            ) : null}
            <PMTabs
              tabs={renderTabs(formikProps)}
              selectedTab={selectedTab}
              setSelectedTab={setSelectedTab}
            />

            <div className="mt-6">
              {renderTabs(formikProps)[selectedTab].component}
            </div>

            <div className="mt-8 flex justify-end space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                {t("Avbryt")}
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
              >
                {loading
                  ? t("Lagrer...")
                  : chemicalInventory
                  ? t("Oppdater")
                  : t("Opprett")}
              </button>
            </div>
          </Form>
        )}
      </Formik>
    </PMDialog>
  );
};
